import Image from "next/image";
import React from "react";
import { cn } from "@/lib/utils";
import { HomepageAssets } from "@/app/components/icm 3.0/homepage-assets";

type IpoListing = {
  name: string;
  /** Industry type shown under the company name (Figma left meta). */
  industry: string;
  Logo: React.ComponentType<{ className?: string }>;
};

const LogoImage = ({
  className,
  src,
}: {
  className?: string;
  src: string;
}) => (
  <Image
    src={src}
    alt=""
    width={54}
    height={54}
    className={cn("bg-white object-contain", className)}
    unoptimized
    aria-hidden
  />
);

const RelianceJioLogo = ({ className }: { className?: string }) => (
  <LogoImage className={className} src={HomepageAssets.ipoRelianceJio} />
);

const AckoLogo = ({ className }: { className?: string }) => (
  <LogoImage className={className} src={HomepageAssets.ipoAcko} />
);

const NSE_LOGO_URL = "https://assets.incredmoney.com/images/v3/NSC%20(1).webp";

const NseLogo = ({ className }: { className?: string }) => (
  <LogoImage className={className} src={NSE_LOGO_URL} />
);

const IPO_LISTINGS: IpoListing[] = [
  {
    name: "Reliance Jio",
    industry: "Telecom",
    Logo: RelianceJioLogo,
  },
  {
    name: "Acko",
    industry: "Insurance",
    Logo: AckoLogo,
  },
  {
    name: "NSE",
    industry: "Financial Services",
    Logo: NseLogo,
  },
];

interface IpoCardProps {
  className?: string;
}

/**
 * IPO product card — desktop 5882:65754; mobile 6679:11580
 * Listings adapted: industry + “To be announced” status; no price; View IPOs CTA hidden.
 */
export function IpoCard({ className }: IpoCardProps) {
  return (
    <article
      className={cn(
        "relative flex h-auto flex-col overflow-hidden rounded-2xl border border-surface-stroke bg-white p-6 md:h-full md:p-[23px]",
        className
      )}
    >
      <div className="relative flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-6">
          <span className="inline-flex h-[30px] w-fit items-center justify-center rounded-lg border border-[#FDE7D7] bg-[#FEF5EE] px-3 py-1 text-sm font-medium leading-5 text-brand-primary-500">
            IPOs
          </span>
          <div className="flex max-w-[236px] flex-col gap-2 tracking-[0] md:max-w-[532px] md:gap-3">
            <h3 className="text-[length:var(--typography-font-size-2xl)] font-bold leading-8 text-black md:whitespace-nowrap md:text-4xl md:leading-[44px]">
              Never miss the next big listing.
            </h3>
            <p className="text-sm leading-5 text-[#767676] md:text-lg md:leading-6">
              Apply to IPOs before they list. Track, apply and invest companies
              before they hit the market.
            </p>
          </div>
        </div>

        <Image
          src={HomepageAssets.ipo}
          alt=""
          width={104}
          height={96}
          className="hidden shrink-0 object-contain md:block md:h-[96px] md:w-[104px]"
          unoptimized
          aria-hidden
        />
      </div>

      <Image
        src={HomepageAssets.ipo}
        alt=""
        width={73}
        height={67}
        className="pointer-events-none absolute left-[calc(50%+127px)] top-[calc(50%+96.5px)] h-[67px] w-[73px] -translate-x-1/2 -translate-y-1/2 object-contain md:hidden"
        unoptimized
        aria-hidden
      />

      <ul className="relative z-[1] mt-6 flex w-full flex-col gap-4 md:mt-auto md:gap-5">
        {IPO_LISTINGS.map(({ name, industry, Logo }) => (
          <li
            key={name}
            className="flex h-[72px] w-full items-center justify-between gap-3 overflow-hidden rounded-lg border border-surface-stroke bg-white px-3 md:h-[82px] md:px-[15px]"
          >
            <div className="flex min-w-0 items-center gap-3 md:gap-4">
              <Logo className="size-10 shrink-0 rounded-md border border-logo-stroke md:size-[54px] md:rounded-[6px] p-2" />
              <div className="flex min-w-0 flex-col gap-1 md:gap-2">
                <p className="truncate text-base font-semibold leading-6 text-black md:text-lg">
                  {name}
                </p>
                <p className="truncate text-sm leading-5 text-[#767676] md:text-base md:leading-[22px]">
                  {industry}
                </p>
              </div>
            </div>

            <p className="shrink-0 text-right text-sm font-regular leading-5 text-brand-primary-500 md:text-base md:leading-[22px]">
              To be announced
            </p>
          </li>
        ))}
      </ul>
    </article>
  );
}
